import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { DateTime } from 'luxon';
import { useCaseSettings } from '@/stores/caseSettings/store';
import { useSurgicalGuideCase } from '@/stores/surgicalGuide/caseStore';
import { SURGICAL_APPROACHES } from '@/domain/case/surgery';
import { useSurgicalGuideOrders } from '@/stores/surgicalGuideOrders/store';
import { isCRNumberValid } from '@/stores/surgicalGuide/surgicalGuideValidation';
import { useUserStore } from '@/stores/userStore';
import { useVersion } from '@/stores/version';

/** Working copy edited by the order dialogue before it is committed to the stores. */
export interface SurgicalGuideOrderForm {
    crNumber: string;
    surgicalApproachId: string;
    surgeryDate: string;
}

/** OSSIS guides require at least 21 days lead time before the surgery date. */
const MIN_LEAD_DAYS = 21;

/** Shown under the form when a save or order placement fails. */
const SUBMIT_ERROR_MESSAGE = 'Something went wrong placing your order. Please try again.';

export function isWithinLeadTime(surgeryDate: string, now: DateTime): boolean {
    return !!surgeryDate && DateTime.fromISO(surgeryDate) < now.plus({ days: MIN_LEAD_DAYS });
}

/**
 * Submitting *requests* a CR number (rather than placing the order) when the
 * user can't enter one and none exists yet — e.g. a surgeon asking Sales to
 * add it later.
 */
export function isCrNumberRequest(form: SurgicalGuideOrderForm, canEditCrNumber: boolean): boolean {
    return !canEditCrNumber && form.crNumber === '';
}

/**
 * Submit-ability depends on the action: a CR-number request needs only the
 * surgery date and approach; placing the order needs all fields, CR included.
 */
export function canSubmitOrder(form: SurgicalGuideOrderForm, canEditCrNumber: boolean): boolean {
    if (form.surgeryDate === '' || form.surgicalApproachId === '') return false;
    if (isCrNumberRequest(form, canEditCrNumber)) return true;
    return form.crNumber !== '' && isCRNumberValid(form.crNumber);
}

// ─── Reactive sections (thin wrappers over the pure logic + stores) ───────

/** Dialog working copy plus its derived validation / UI state. */
function useOrderForm() {
    const caseSettings = useCaseSettings();
    const userStore = useUserStore();

    const form = reactive<SurgicalGuideOrderForm>({
        crNumber: '',
        surgicalApproachId: '',
        surgeryDate: '',
    });

    return {
        form,
        surgicalApproachOptions: computed(() => SURGICAL_APPROACHES),
        showCrNumber: computed(() => userStore.canEditCrNumber),
        isRequestingCrNumber: computed(() => isCrNumberRequest(form, userStore.canEditCrNumber)),
        surgeryDateDisabled: computed(() => caseSettings.isCaseReadonly),
        crNumberInvalid: computed(() => !isCRNumberValid(form.crNumber)),
        threeWeeksWarning: computed(() => isWithinLeadTime(form.surgeryDate, DateTime.now())),
        disableSubmit: computed(() => !canSubmitOrder(form, userStore.canEditCrNumber)),
    };
}

/** Dialog visibility plus the open / cancel / submit lifecycle. */
function useOrderSubmission(form: SurgicalGuideOrderForm) {
    const caseSettings = useCaseSettings();
    const caseSurgicalGuide = useSurgicalGuideCase();
    const orders = useSurgicalGuideOrders();
    const userStore = useUserStore();
    const versionStore = useVersion();

    const isOpen = ref(false);
    const isSaving = ref(false);
    const submitError = ref('');
    let targetCaseId: number | null = null;
    let targetPlanId: number | null = null;

    function openOrder(caseId: number, planId: number): void {
        targetCaseId = caseId;
        targetPlanId = planId;
        submitError.value = '';
        // Seed the working copy from the case-level stores.
        form.crNumber = caseSurgicalGuide.current.crNumber;
        form.surgicalApproachId = caseSurgicalGuide.current.surgicalApproachId;
        form.surgeryDate = caseSettings.case.surgery.date;
        isOpen.value = true;
    }

    function cancelOrder(): void {
        // Working copy is discarded; nothing was written to the stores.
        isOpen.value = false;
    }

    /** Clears dialog state — called when the host view unmounts so a stale
     *  open dialog (this is a page-level singleton) can't survive navigation. */
    function reset(): void {
        isOpen.value = false;
        isSaving.value = false;
        submitError.value = '';
    }

    async function submitOrder(): Promise<void> {
        if (targetCaseId === null || targetPlanId === null) return;
        // Guard against a double-submit placing duplicate orders.
        if (isSaving.value) return;
        const caseId = targetCaseId;
        const planId = targetPlanId;

        isSaving.value = true;
        submitError.value = '';
        // Commit the working copy, then let each Model's dirty flag decide on a save.
        caseSurgicalGuide.current.crNumber = form.crNumber;
        caseSurgicalGuide.current.surgicalApproachId = form.surgicalApproachId;
        caseSettings.case.surgery.date = form.surgeryDate;
        try {
            if (caseSettings.isDirty) {
                await caseSettings.save(false, versionStore.webComponentVersion);
            }
            if (caseSurgicalGuide.hasUnsavedChanges) {
                await caseSurgicalGuide.save(caseId, userStore.canEditCrNumber);
            }
            await orders.placeOrder(caseId, planId);
            await orders.load(caseId);
            orders.newOrderPlanId = planId;
            isOpen.value = false;
        } catch {
            // A successful step commits to its store, so the dirty-flag guards
            // above skip it on retry — only the failed step re-runs. Keep the
            // dialog open and surface the error against the preserved working copy.
            submitError.value = SUBMIT_ERROR_MESSAGE;
        } finally {
            // Always clear the in-flight flag so the user can retry.
            isSaving.value = false;
        }
    }

    return { isOpen, isSaving, submitError, openOrder, cancelOrder, submitOrder, reset };
}

/**
 * Page-level singleton for the "Order PSI Guide" dialog.
 *
 * Owns the dialog's mutable state (working-copy form + visibility), so it is a
 * store. A plan card calls `openOrder(caseId, planId)` to seed and show it; the
 * single page-level modal reads its form/validation. Per-plan order *display*
 * is stateless and lives in `usePlanOrderDisplay`.
 */
export const useSurgicalGuideOrderDialog = defineStore('surgical-guide-order-dialog', () => {
    const formParts = useOrderForm();
    return {
        ...formParts,
        ...useOrderSubmission(formParts.form),
    };
});